import { useEffect, useContext, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { loginRequest, checkLoggedIn } from "../../authConfig";
import { LoginContext } from "../../loginContext";
import styles from "./Login.module.css";
import abbLogo from "../../assets/abb-logo.svg";

export const Login = () => {
    const { instance } = useMsal();
    const { setLoggedIn } = useContext(LoginContext);
    const { t } = useTranslation();
    const redirectAttempted = useRef(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const checkAndRedirect = async () => {
            // If a redirect was already attempted in this mount cycle, don't try again
            // This prevents the infinite loop: app → Microsoft → app → Microsoft → ...
            if (redirectAttempted.current) {
                return;
            }

            const isLoggedIn = await checkLoggedIn(instance);
            if (cancelled) return;

            if (isLoggedIn) {
                setLoggedIn(true);
            } else {
                // Mark that we're about to attempt a redirect
                redirectAttempted.current = true;
                try {
                    // User is not logged in, initiate redirect to Microsoft login
                    await instance.loginRedirect(loginRequest);
                } catch (error) {
                    console.error("Login redirect failed:", error);
                    if (!cancelled) {
                        setLoginError(String(error));
                    }
                }
            }
        };

        checkAndRedirect();

        return () => {
            cancelled = true;
        };
    }, [instance, setLoggedIn]);

    return (
        <div className={styles.loginContainer}>
            <div className={styles.loginBox}>
                <img src={abbLogo} alt="ABB logo" className={styles.logo} />
                <h1 className={styles.title}>{t("appTitle")}</h1>
                <p className={styles.subtitle}>{t("loginSubtitle")}</p>

                {loginError ? (
                    <div style={{ textAlign: "center" }}>
                        <p style={{ color: "red", marginBottom: "16px" }}>Login failed. Please try again.</p>
                        <DefaultButton
                            text={t("login")}
                            onClick={() => {
                                setLoginError(null);
                                redirectAttempted.current = false;
                                instance.loginRedirect(loginRequest).catch(err => {
                                    console.error("Login redirect failed:", err);
                                    setLoginError(String(err));
                                });
                            }}
                        />
                    </div>
                ) : (
                    <Spinner size={SpinnerSize.large} label={t("redirecting")} />
                )}
            </div>
        </div>
    );
};

export default Login;
